import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AppInstance from '../../api/GlobalAxios';
import ApiUtil from '../../api/ApiUtil';
import { toast } from 'react-toastify';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Image from '@editorjs/image';
import Code from '@editorjs/code';
import Link from '@editorjs/link';
import Quote from '@editorjs/quote';

// Import base URL for API calls
const BASEURL = "https://flooringdeals.ca/api";
//const BASEURL = "http://localhost:5001/api";

const BlogEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [blog, setBlog] = useState({
        title: '',
        excerpt: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: [],
        tags: [],
        status: 'draft'
    });
    const [initialEditorData, setInitialEditorData] = useState({}); // Added for Editor.js initial data
    const [featuredImage, setFeaturedImage] = useState(null);
    const [featuredImagePreview, setFeaturedImagePreview] = useState('');
    const editorInstance = useRef(null);
    const editorContainerRef = useRef(null);

    // Helper function to get the correct image source URL
    const getImageSrc = (imageUrl) => {
        // If it's already an absolute URL (S3), use it as is
        if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
            return imageUrl;
        }
        // Otherwise, it's a local file path, prepend base URL
        return `http://localhost:5001${imageUrl}`;
    };

    const handleFeaturedImageChange = (e) => {
        const file = e.target.files[0];
        setFeaturedImage(file);
        
        // Create preview URL for newly selected file
        if (file) {
            const fileURL = URL.createObjectURL(file);
            setFeaturedImagePreview(fileURL);
        } else {
            // If no file selected, reset preview
            setFeaturedImagePreview('');
        }
    };

    const fetchBlog = useCallback(async () => {
        if (!id) return;
        setLoading(true); 
        try {
            // Use ApiUtil with GlobalAxios instance which automatically adds auth headers
            const response = await ApiUtil.SendAsync({
                url: `/blogs/id/${id}`,
                method: 'GET'
            });
            
            // Check if response exists and has data property
            if (!response || !response.data) {
                throw new Error('Invalid response from API');
            }
            
            const fetchedBlog = response.data;
            setBlog({ 
                title: fetchedBlog.title || '',
                excerpt: fetchedBlog.excerpt || '',
                metaTitle: fetchedBlog.metaTitle || '',
                metaDescription: fetchedBlog.metaDescription || '',
                metaKeywords: fetchedBlog.metaKeywords || [],
                tags: fetchedBlog.tags || [],
                status: fetchedBlog.status || 'draft'
            });
            setInitialEditorData(fetchedBlog.body || {});
            
            // Store the URL of the existing featured image (if any)
            if (fetchedBlog.featuredImage) {
                // Just store the URL for display, don't set as a File object
                setFeaturedImagePreview(fetchedBlog.featuredImage);
            }
            // setLoading(false) will be handled by the calling effect or after editor sync
        } catch (error) {
            toast.error('Error fetching blog');
            setInitialEditorData({});
        } finally {
            setLoading(false); // Ensure loading is set to false after fetch attempt
        }
    }, [id]);

    // Effect 1: Handle mode (new/edit), fetch data for edit, set loading states
    useEffect(() => {
        if (id) { // Edit mode
            fetchBlog();
        } else { // New post mode
            setBlog({
                title: '', excerpt: '', metaTitle: '', metaDescription: '',
                metaKeywords: [], tags: [], status: 'draft'
            });
            setFeaturedImage(null);
            setFeaturedImagePreview('');
            setInitialEditorData({}); 
            setLoading(false); 
        }
    }, [id, fetchBlog]);

    // Effect 2: Initialize EditorJS once the container is ready and not loading
    useEffect(() => {
        if (loading || !editorContainerRef.current) {
            return;
        }

        if (editorInstance.current) {
            try {
                editorInstance.current.destroy();
            } catch (e) {
                console.error("Error destroying previous editor instance:", e);
            } finally {
                editorInstance.current = null;
            }
        }
        
        // Get user data for image upload authorization
        const userStr = localStorage.getItem('user');
        let token = null;
        if (userStr) {
            try {
                const userData = JSON.parse(userStr);
                token = userData.token;
            } catch (e) {
                console.error('Error parsing user data:', e);
            }
        }
        
        const editor = new EditorJS({
            holder: editorContainerRef.current,
            tools: {
                header: { class: Header, config: { levels: [2, 3, 4], defaultLevel: 2 }},
                list: { class: List, inlineToolbar: true },
                image: { 
                    class: Image, 
                    config: { 
                        endpoints: { 
                            // Use the same base URL as GlobalAxios, but with the non-authenticated test endpoint
                            byFile: BASEURL + '/upload-image-test' 
                        }
                    }
                },
                code: Code,
                linkTool: Link,
                quote: Quote
            },
            placeholder: 'Start writing your blog post...',
            data: initialEditorData,
            async onReady() {
                // console.log('Editor.js is ready');
            },
        });
        editorInstance.current = editor;

        return () => {
            if (editorInstance.current) {
                try {
                    editorInstance.current.destroy();
                } catch (e) {
                    console.error("Error destroying editor instance on cleanup:", e);
                } finally {
                    editorInstance.current = null;
                }
            }
        };
    }, [loading, initialEditorData]); // Key dependencies

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate excerpt length before submission
        if (blog.excerpt.length > 300) {
            toast.error('Excerpt cannot exceed 300 characters. Please shorten it before submitting.');
            return;
        }
        
        try {
            const editorData = await editorInstance.current.save();
            
            const formData = new FormData();
            formData.append('title', blog.title);
            formData.append('excerpt', blog.excerpt);
            formData.append('metaTitle', blog.metaTitle);
            formData.append('metaDescription', blog.metaDescription);
            formData.append('metaKeywords', JSON.stringify(blog.metaKeywords));
            formData.append('tags', JSON.stringify(blog.tags));
            formData.append('status', blog.status);
            formData.append('body', JSON.stringify(editorData)); // Changed 'content' to 'body'
            
            // Only append featuredImage if a file is selected
            if (featuredImage instanceof File) {
                formData.append('featuredImage', featuredImage);
            }
            
            // Log form data for debugging
            console.log('Form data being sent:');
            for (let [key, value] of formData.entries()) {
                console.log(key, value);
            }

            // Check if user is logged in
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                toast.error('You need to be logged in. Please log in again.');
                navigate('/');
                return;
            }
            
            // Print login data to console for debugging
            try {
                const userData = JSON.parse(userStr);
                console.log('User data:', userData);
            } catch (e) {
                console.error('Error parsing user data:', e);
            }

            // Use FormData with proper content-type header
            // Note: AppInstance will automatically add the auth token from localStorage
            if (id) {
                const response = await ApiUtil.SendAsync({
                    url: `/blogs/${id}`,
                    method: 'PUT',
                    data: formData
                });
                
                if (response) {
                    toast.success('Blog updated successfully');
                    navigate('/admin/blogs');
                }
            } else {
                const response = await ApiUtil.SendAsync({
                    url: '/blogs',
                    method: 'POST',
                    data: formData
                });
                
                if (response) {
                    toast.success('Blog created successfully');
                    navigate('/admin/blogs');
                }
            }
            
        } catch (error) {
            console.error('Error saving blog:', error);
            
            // Handle different error scenarios
            if (error.status === 401 || (error.response && error.response.status === 401)) {
                toast.error('Unauthorized. Your session may have expired. Please log in again.');
                navigate('/'); // Redirect to login page
            } else if (error.status === 403 || (error.response && error.response.status === 403)) {
                toast.error('Forbidden. You do not have permission to perform this action.');
            } else if (error.message) {
                toast.error(`Error: ${error.message}`);
            } else {
                toast.error('Error saving blog. Please try again.');
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'tags' || name === 'metaKeywords') {
            setBlog({
                ...blog,
                [name]: value.split(',').map(item => item.trim())
            });
        } else if (name === 'excerpt') {
            // For excerpt, we'll allow typing but show warning if over limit
            setBlog({
                ...blog,
                [name]: value
            });
        } else {
            setBlog({
                ...blog,
                [name]: value
            });
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">
                {id ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                        type="text"
                        name="title"
                        value={blog.title}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Excerpt 
                        <span className={`text-xs ml-2 ${
                            blog.excerpt.length > 300 ? 'text-red-600 font-medium' :
                            blog.excerpt.length > 250 ? 'text-orange-600' : 'text-gray-500'
                        }`}>
                            ({blog.excerpt.length}/300 characters)
                        </span>
                    </label>
                    <textarea
                        name="excerpt"
                        value={blog.excerpt}
                        onChange={handleInputChange}
                        rows="3"
                        className={`mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                            blog.excerpt.length > 300 ? 'border-red-500 bg-red-50' : 
                            blog.excerpt.length > 250 ? 'border-orange-300' : 'border-gray-300'
                        }`}
                        placeholder="A brief description of the blog post... (max 300 characters)"
                    />
                    {blog.excerpt.length > 300 && (
                        <p className="text-red-500 text-sm mt-1">
                            ⚠️ Excerpt exceeds 300 character limit. Please shorten it to submit.
                        </p>
                    )}
                    {blog.excerpt.length > 250 && blog.excerpt.length <= 300 && (
                        <p className="text-orange-600 text-sm mt-1">
                            ⚡ Approaching character limit ({300 - blog.excerpt.length} characters remaining)
                        </p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Featured Image
                    </label>
                    {featuredImagePreview && (
                        <div className="mb-2">
                            <img 
                                src={getImageSrc(featuredImagePreview)} 
                                alt="Featured" 
                                className="h-32 object-cover rounded-md mb-2"
                            />
                            <p className="text-xs text-gray-500">Current featured image</p>
                        </div>
                    )}
                    <input
                        type="file"
                        onChange={handleFeaturedImageChange}
                        className="mt-1 block w-full"
                        accept="image/*"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        {id ? 'Leave empty to keep current image' : 'Select an image for your blog post'}
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Meta Title
                    </label>
                    <input
                        type="text"
                        name="metaTitle"
                        value={blog.metaTitle}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Meta Description
                    </label>
                    <textarea
                        name="metaDescription"
                        value={blog.metaDescription}
                        onChange={handleInputChange}
                        rows="3"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Meta Keywords (comma-separated)
                    </label>
                    <input
                        type="text"
                        name="metaKeywords"
                        value={blog.metaKeywords.join(', ')}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">
                        Tags (comma-separated)
                    </label>
                    <input
                        type="text"
                        name="tags"
                        value={blog.tags.join(', ')}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                        name="status"
                        value={blog.status}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content
                    </label>
                    <div
                        ref={editorContainerRef}
                        id="editorjs"
                        className="border rounded-md min-h-[400px] p-4"
                    />
                </div>

                <div className="flex justify-end space-x-4">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/blogs')}
                        className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={blog.excerpt.length > 300}
                        className={`px-4 py-2 rounded-md text-white ${
                            blog.excerpt.length > 300 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                    >
                        {id ? 'Update' : 'Create'} Blog Post
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BlogEditor;